import React, { useState, useEffect } from 'react';
import { 
  MagnifyingGlassIcon, 
  MapPinIcon, 
  StarIcon,
  FunnelIcon,
  ViewColumnsIcon,
  ListBulletIcon 
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

interface Seller {
  seller_id: number;
  business_name: string;
  contact_name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  latitude: number;
  longitude: number;
  business_type: string;
  profile_type: string;
  verification_status: string;
  rating: number;
  review_count: number;
  image_url?: string;
  specialties: string[];
  inventory_status: string;
  distance?: number;
}

interface FilterOptions {
  businessType: string;
  state: string;
  verificationStatus: string;
  inventoryStatus: string;
  rating: number;
  distance: number;
}

const SellerDirectory: React.FC = () => {
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [filteredSellers, setFilteredSellers] = useState<Seller[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sellersPerPage] = useState(12);

  const [filters, setFilters] = useState<FilterOptions>({
    businessType: '',
    state: '',
    verificationStatus: '',
    inventoryStatus: '',
    rating: 0,
    distance: 0
  });

  // Fetch sellers on component mount
  useEffect(() => {
    const fetchSellers = async () => {
      try {
        const response = await fetch('/api/sellers');
        if (!response.ok) {
          throw new Error('Failed to fetch sellers');
        }
        const data = await response.json();
        setSellers(data);
        setFilteredSellers(data);
      } catch (error) {
        console.error('Error fetching sellers:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSellers();
  }, []);

  // Apply filters and search
  useEffect(() => {
    let filtered = [...sellers];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(seller =>
        seller.business_name.toLowerCase().includes(query) ||
        seller.contact_name.toLowerCase().includes(query) ||
        seller.city.toLowerCase().includes(query) ||
        seller.state.toLowerCase().includes(query) ||
        seller.specialties.some(specialty => specialty.toLowerCase().includes(query))
      );
    }

    // Business type filter
    if (filters.businessType) {
      filtered = filtered.filter(seller => seller.business_type === filters.businessType);
    }

    // State filter
    if (filters.state) {
      filtered = filtered.filter(seller => seller.state === filters.state);
    }

    // Verification status filter
    if (filters.verificationStatus) {
      filtered = filtered.filter(seller => seller.verification_status === filters.verificationStatus);
    }

    // Inventory status filter
    if (filters.inventoryStatus) {
      filtered = filtered.filter(seller => seller.inventory_status === filters.inventoryStatus);
    }

    // Rating filter
    if (filters.rating > 0) {
      filtered = filtered.filter(seller => seller.rating >= filters.rating);
    }

    // Distance filter (would need user location)
    if (filters.distance > 0 && filtered.some(s => s.distance)) {
      filtered = filtered.filter(seller => !seller.distance || seller.distance <= filters.distance);
    }

    setFilteredSellers(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [sellers, searchQuery, filters]);

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <div key={star}>
            {star <= rating ? (
              <StarIconSolid className="w-4 h-4 text-yellow-400" />
            ) : (
              <StarIcon className="w-4 h-4 text-gray-300" />
            )}
          </div>
        ))}
        <span className="ml-1 text-sm text-gray-600">({rating.toFixed(1)})</span>
      </div>
    );
  };

  // Pagination
  const indexOfLastSeller = currentPage * sellersPerPage;
  const indexOfFirstSeller = indexOfLastSeller - sellersPerPage;
  const currentSellers = filteredSellers.slice(indexOfFirstSeller, indexOfLastSeller);
  const totalPages = Math.ceil(filteredSellers.length / sellersPerPage);

  const businessTypes = [...new Set(sellers.map(s => s.business_type))];
  const states = [...new Set(sellers.map(s => s.state))].sort();

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading seller directory...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Seller Directory</h1>
        <p className="text-lg text-gray-600">
          Browse for Local Nucs & Queens
        </p>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        {/* Search Bar */}
        <div className="relative mb-4">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by business name, contact, location, or specialty..."
            className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
          />
          <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        </div>

        {/* Filter Controls */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <FunnelIcon className="w-5 h-5" />
            <span>Filters</span>
            {Object.values(filters).some(v => v) && (
              <span className="bg-amber-500 text-white text-xs px-2 py-1 rounded-full">
                {Object.values(filters).filter(v => v).length}
              </span>
            )}
          </button>

          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600">
              {filteredSellers.length} seller{filteredSellers.length !== 1 ? 's' : ''} found
            </span>
            
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
              >
                <ViewColumnsIcon className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
              >
                <ListBulletIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Filter Options */}
        {showFilters && (
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-6 border-t border-gray-200">
            <select
              value={filters.businessType}
              onChange={(e) => setFilters(prev => ({ ...prev, businessType: e.target.value }))}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value="">All Business Types</option>
              {businessTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>

            <select
              value={filters.state}
              onChange={(e) => setFilters(prev => ({ ...prev, state: e.target.value }))}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value="">All States</option>
              {states.map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>

            <select
              value={filters.verificationStatus}
              onChange={(e) => setFilters(prev => ({ ...prev, verificationStatus: e.target.value }))}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value="">All Verification Status</option>
              <option value="verified">Verified</option>
              <option value="pending">Pending</option>
            </select>

            <select
              value={filters.inventoryStatus}
              onChange={(e) => setFilters(prev => ({ ...prev, inventoryStatus: e.target.value }))}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value="">All Inventory Status</option>
              <option value="in_stock">In Stock</option>
              <option value="low_stock">Low Stock</option>
              <option value="out_of_stock">Out of Stock</option>
              <option value="seasonal">Seasonal</option>
            </select>

            <select
              value={filters.rating}
              onChange={(e) => setFilters(prev => ({ ...prev, rating: Number(e.target.value) }))}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value={0}>All Ratings</option>
              <option value={4}>4+ Stars</option>
              <option value={3}>3+ Stars</option>
              <option value={2}>2+ Stars</option>
            </select>

            <button
              onClick={() => setFilters({
                businessType: '',
                state: '',
                verificationStatus: '',
                inventoryStatus: '',
                rating: 0,
                distance: 0
              })}
              className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Sellers Display */}
      {viewMode === 'grid' ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {currentSellers.map((seller) => (
            <div 
              key={seller.seller_id}
              className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer overflow-hidden"
              onClick={() => window.location.href = `/seller/${seller.seller_id}`}
            >
              <div className="h-48 bg-gradient-to-br from-amber-100 to-orange-100 relative">
                {seller.image_url ? (
                  <img 
                    src={seller.image_url} 
                    alt={seller.business_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-5xl">🐝</div>
                )}
                
                {seller.profile_type === 'premium' && (
                  <div className="absolute top-2 right-2 bg-amber-500 text-white px-2 py-1 rounded-full text-xs">
                    PREMIUM
                  </div>
                )}
              </div>

              <div className="p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {seller.business_name}
                </h3>
                
                <div className="flex items-center text-gray-600 mb-2">
                  <MapPinIcon className="w-4 h-4 mr-1" />
                  <span className="text-sm">{seller.city}, {seller.state}</span>
                </div>

                <div className="mb-3">
                  {renderStars(seller.rating)}
                </div>

                <div className="flex flex-wrap gap-1 mb-3">
                  {seller.specialties.slice(0, 2).map((specialty, index) => (
                    <span 
                      key={index}
                      className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded"
                    >
                      {specialty}
                    </span>
                  ))}
                </div>

                <div className="flex items-center justify-between">
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    seller.inventory_status === 'in_stock' ? 'bg-green-100 text-green-800' :
                    seller.inventory_status === 'low_stock' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {seller.inventory_status.replace('_', ' ').toUpperCase()}
                  </span>
                  
                  <span className="text-blue-600 text-sm font-medium">
                    View Details →
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md mb-8">
          {currentSellers.map((seller, index) => (
            <div 
              key={seller.seller_id}
              className={`p-6 cursor-pointer hover:bg-gray-50 transition-colors ${
                index < currentSellers.length - 1 ? 'border-b border-gray-200' : ''
              }`}
              onClick={() => window.location.href = `/seller/${seller.seller_id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-orange-100 rounded-lg flex items-center justify-center text-2xl">
                      {seller.image_url ? (
                        <img 
                          src={seller.image_url} 
                          alt={seller.business_name}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      ) : '🐝'}
                    </div>
                    
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {seller.business_name}
                      </h3>
                      <div className="flex items-center text-gray-600 mt-1">
                        <MapPinIcon className="w-4 h-4 mr-1" />
                        <span className="text-sm">{seller.city}, {seller.state}</span>
                      </div>
                      <div className="mt-2">
                        {renderStars(seller.rating)}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="text-right">
                  <span className={`inline-block text-xs px-2 py-1 rounded-full mb-2 ${
                    seller.inventory_status === 'in_stock' ? 'bg-green-100 text-green-800' :
                    seller.inventory_status === 'low_stock' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {seller.inventory_status.replace('_', ' ').toUpperCase()}
                  </span>
                  <div className="text-blue-600 text-sm font-medium">
                    View Details →
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center space-x-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Previous
          </button>
          
          {[...Array(totalPages)].map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentPage(index + 1)}
              className={`px-4 py-2 border rounded-lg ${
                currentPage === index + 1
                  ? 'bg-amber-600 text-white border-amber-600'
                  : 'border-gray-300 hover:bg-gray-50'
              }`}
            >
              {index + 1}
            </button>
          ))}
          
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default SellerDirectory;